<template>
    <div>
        <b-alert :show="!!errorHandlingMessage" variant="danger" dismissible>{{ errorHandlingMessage }}</b-alert>
        <b-overlay :show="!formReady">
            <div v-if="!formReady" class="min-height-200">Loading...</div>
            <FormulateForm
                v-if="formReady"
                :values="initialFormData"
                :name="formName"
                :form-errors="formErrors"
                :keep-model-data="true"
                #default="{ isValid, isLoading, value }"
                @submit="handleSubmit"
            >   
                <FormulateInput
                    id="compsubfile-formulate-file-input"
                    ref="formulateFileInput"
                    type="file"
                    name="files"
                    key="files"
                    label="Files"
                    :validation="fileRequired ? 'required' : ''"
                    validation-name="File(s)"
                    :disabled="isLocked"
                    :file-remove-class="isLocked ? 'd-none' : []"
                    :upload-area-mask-class="['bg-gray-100']"
                    :outer-class="['formulate-horizontal']"
                    :uploader="formulateUploader"
                    upload-behavior="live"
                    multiple     
                />
                <FormulateInput
                    name="notes"
                    key="notes"
                    type="textarea"
                    label="Notes about this version"
                    placeholder="Add any notes about this specific version.  Visible internally and externally"
                    rows="2"
                    help="Optional"
                    validation-name="Notes"
                    :disabled="isLocked"
                    :outer-class="['formulate-horizontal']"
                />
                <hr>
                <FormulateInput
                    name="supercede_others"
                    key="supercede_others"
                    type="checkbox"
                    label="Supercede previous versions?"
                    class="mb-1"
                ></FormulateInput>
                <hr>
                <!-- <FormulateInput
                    name="enableExpiration"
                    key="enableExpiration"
                    type="checkbox"
                    label="Track Expiration / Valid Dates?"
                    class="mb-1"
                ></FormulateInput> -->
                <FormulateInput
                    name="enableExpirationChoice"
                    key="enableExpirationChoice"
                    type="radio"
                    label="Track Expiration / Valid Dates?"
                    :options="{yes: 'Yes', no: 'No'}"
                    class="mb-1"
                    :wrapper-class="['d-flex', 'gap-4']"
                    :element-class="['d-flex', 'gap-3']"
                />
                <div v-if="value && value.enableExpirationChoice == 'yes'" class="">
                    <div class="row">
                        <div class="col-6">
                            <FormulateInput
                                name="validFromMode"
                                key="validFromMode"
                                type="radio"
                                label="Valid From"
                                :options="[
                                    { value: 'forever', label: 'No Limit' },
                                    { value: 'date', label: 'Specific Date' },
                                ]"
                                validation="required"
                                validation-name="Valid From selection"
                                :disabled="isLocked"
                                :label-class="['font-size-100', 'mb-3', 'pb-2', 'border-bottom']"
                            ></FormulateInput>
                            <FormulateInput
                                v-if="value && value.validFromMode === 'date'"
                                name="valid_from"
                                key="valid_from"
                                type="bootstrap_date"
                                inline
                                label="Valid From Date"
                                validation="required"
                                validation-name="Valid From date"
                                :disabled="isLocked"
                            ></FormulateInput>
                        </div>
                        <div class="col-6">
                            <FormulateInput
                                name="validToMode"
                                key="validToMode"
                                type="radio"
                                label="Valid To"
                                :options="[
                                    { value: 'forever', label: 'No Limit' },
                                    { value: 'date', label: 'Specific Date' },
                                ]"
                                validation="required"
                                validation-name="Valid To selection"
                                :disabled="isLocked"
                                :label-class="['font-size-100', 'mb-3', 'pb-2', 'border-bottom']"
                            ></FormulateInput>
                            <FormulateInput
                                v-if="value && value.validToMode === 'date'"
                                name="valid_to"
                                key="valid_to"
                                type="bootstrap_date"
                                inline
                                label="Valid To Date"
                                validation="required"
                                validation-name="Valid To date"
                                :disabled="isLocked"
                            ></FormulateInput>
                        </div>
                    </div>
                </div>

                <FormulateErrors />

                <div class="mt-4 mb-3 d-flex justify-content-center">
                    <b-button
                        variant="gray-secondary"
                        size="lg"
                        class="mr-3"
                        @click="triggerCancel"
                    >
                        <span>Cancel</span>
                    </b-button>
                    <b-button
                        variant="primary"
                        size="lg"
                        :disabled="!isValid || isLoading || saving || uploadPending || isLocked"
                        @click="triggerSubmit"
                    >
                        <b-spinner small v-if="saving" key="uploading" class="mr-2"></b-spinner>
                        <b-icon v-else key="uploadReady" icon="upload" class="mr-2"></b-icon>
                        <span>{{ saving ? 'Saving' : 'Save' }}</span>
                    </b-button>
                </div>
            </FormulateForm>
        </b-overlay>
    </div>
</template>

<script>
import { errorHandling } from '../../js/custom-vue-mixins';
import { suppliosApi } from '../../js/api-service';
import { asyncSplFileSlinger } from '../../js/file-helpers';
import cloneDeep from 'lodash/cloneDeep';
import { DateTime } from 'luxon';

// const emptyReminderData = {
//     duration_num_periods: 0,
//     duration_period: 'days',
//     reminder_action: '',
// };

const defaultData = {
    // title: '',
    // comp_item_instructions: '',
    // comp_item_notes: '',
    // comp_item_type_slug: '',
    notes: '',
    // EXPIRATION -- pop stuff off before sending to API 
    //enableExpiration: true,  // default to have this ON
    enableExpirationChoice: 'yes',  // REPLACES enableExpiration boolean
    validFromMode: 'forever',  // forever or date
    validToMode: 'forever',  // forever or date
    valid_from: null,
    valid_to: null,
    // valid_from_forever: true,
    // valid_to_forever: true,
    // REMINDERS
    //enableExpireActions: false,
    supercede_others: true,  // only relevant for making new submissions on existing items
    //...emptyReminderData,
    // duration_num_periods: 0,
    // duration_period: 'days',
    // reminder_action: '',
};

// const expireActionPeriodOptions = [  // TEMPORARY - should be fetched from API
//     { value: 'days', label: 'Days' },
//     { value: 'weeks', label: 'Weeks' },
//     { value: 'months', label: 'Months' },
// ];

// const expireActionOptions = [  // TEMPORARY - should be fetched from API
//     //{ value: 'email_supplier', label: 'Email Supplier' },
//     { value: 'email_me', label: 'Assign Follow-Up Task To Me' },  // CREATE A NODE, with a saved message, just deliver that
//     //{ value: 'start_renew', label: 'Start Renewal' },
// ];

// const itemTypeOptions = [  // TEMPORARY - should be fetched from API
//     { value: 'contract', label: 'Contract' },
//     { value: 'certificate', label: 'Certificate' },
//     //{ value: 'acknowledgement', label: 'Acknowledgement' },
//     //{ value: 'form', label: 'Form' },
// ];

// NOTE - this is the OLD version, when we created new CompItems with each submission for one-offs...
// this has been replaced by VendorCompItemForm.vue

export default {
    name: 'CompSubForm',
    components: {},
    mixins: [
        errorHandling,
    ],
    props: {
        vendorCompItemId: {  // should be VCI ID now, and should ALWAYS be passed in
            type: String,
            required: true,
        },
        fileRequired: {  // are files required on this form, based on settings in the comp item?
            type: Boolean,
            default: false,
        },
        existingData: {  // IF DOING UPDATE - handle this later!  TODO - needs work if using for updates!  and should not be allowed to update versions...
            type: Object,
            required: false,
        },
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,  
            saving: false,
            loadingItemTypes: false,
            formName: 'compSubForm',
            formReady: false,  // set true when you're done prepping initial data, if any
            formErrors: [],  // for form-level errors
            uploadPending: false,  // are uploads happening?
            initialFormData: {},  // if you are updating existing, must put here (goes to value prop, not v-model)
            //formData: {},  // just set initialFormData - then get the 'current' data from the @submit handler
            fileCategory: 'comp_sub',  // the s3file category in our backend 
        }
    },
    computed: {
        isLocked() {
            // do we want to lock form?  add logic later...
            return false;
        },
        isUpdate() {
            return !!this.existingData?.id;
        },
    },
    mounted() {
        // do something depending on being a create/update?
        if (this.isUpdate) {
            this.setInitialFormData(this.existingData);
            this.formReady = true;
        } else {
            this.setInitialFormData(defaultData);
            this.formReady = true;
        }
    },
    methods: {
        // use this.apiErrorHandler(error, extraMsg, localMsg) from mixin - localMsg is bool, if true puts message in this.errorHandlingMessage
        setInitialFormData(subData) {
            // TODO -- if you actualy start using for updates, make sure any existing DATES are in tenant tz correctly
            // pass in existingData from your API, this has to format the 'files' array as Formulate expects
            // multiple file inputs should be an array of objects with at least:
            //   'id' (for us), 'url' key (for formulate) and 'name' is optional (should be filename)
            // our INCOMING data (from output serializer has 'files_list' - nested array of files, and 'files' - just the IDs, used for updates)
            // we need to get the object data from 'files_list' and set it on 'files' here for use in the form
            // THEN, before sending to API, make a flat list of JUST THE IDS of new/updated IDs
            let { files_list, ...otherData } = subData;
            this.initialFormData = cloneDeep(otherData);  // copy this first, then we'll add files back in after 
            if (files_list && Array.isArray(files_list) && files_list.length) {
                let initialFiles = [];
                files_list.forEach(f => {
                    if (!f.id || !f.download_url) {  // could also just use 'public_url' - but this person should have perms to get download_url...
                        return;  // GOTTA HAVE 'id' (for our own backend) and 'url' for formulate
                    }
                    const fileObj = {id: f.id, url: f.download_url};
                    if (f.filename) {
                        fileObj['name'] = f.filename;
                    }
                    initialFiles.push(fileObj);
                })
                this.initialFormData['files'] = initialFiles;  // as long as 'files' is still the key you want it as when posting to API
            }
        },
        triggerSubmit() {
            this.$formulate.submit(this.formName);
        },
        triggerCancel() {
            this.$emit('cancel');  // also need to reset things?  or let it get destroyed?
        },
        formatDataForSave(formData) {
            // do anything with the data before sending to API
            // must get the file IDs out and send those in array of 'files'
            console.log('data we need to format for save:', formData);
            const errors = [];  // just a list of strings, we will set on :form-errors (the form-level prop)
            // handle valid_from, valid_to, reminders, etc.
            let { validFromMode, validToMode, valid_from, valid_to, enableExpirationChoice, ...saveData } = cloneDeep(formData);
            let enableExpiration = enableExpirationChoice === 'yes';  // change our choice to boolean for logic below
            //enableExpireActions = enableExpireActions && enableExpiration;  // if we're not tracking expiration, we can't set reminders
            // update saveData
            if (!enableExpiration) {
                saveData['valid_from'] = null;
                saveData['valid_to'] = null;
                saveData['valid_from_forever'] = true;
                saveData['valid_to_forever'] = true;
            } else {
                if (validFromMode === 'forever') {
                    saveData['valid_from'] = null;
                    saveData['valid_from_forever'] = true;
                } else {
                    saveData['valid_from'] = valid_from;
                    saveData['valid_from_forever'] = false;
                }
                if (validToMode === 'forever') {
                    saveData['valid_to'] = null;
                    saveData['valid_to_forever'] = true;
                } else {
                    saveData['valid_to'] = valid_to;
                    saveData['valid_to_forever'] = false;
                }
            }
            // check valid_from/valid_to relative to each other 
            // why check here?  because we don't have a reliable way to look at form data until it submits (because formulate is crappy)
            if (saveData['valid_from'] && saveData['valid_to']) {
                const fromDT = DateTime.fromISO(saveData['valid_from']);
                const toDT = DateTime.fromISO(saveData['valid_to']);
                if (fromDT > toDT) {
                    errors.push('Valid From date must be before Valid To date');
                }
            }
            // NO REMINDERS IN SUBMISSION-ONLY FORM
            // if (!this.submissionOnly) {
            //     if (enableExpireActions) {
            //         saveData['expire_action_data'] = {
            //             duration_num_periods: duration_num_periods,
            //             duration_period: duration_period,
            //             reminder_action: reminder_action,
            //         }  // TODO - add more validation here
            //     } else {
            //         saveData['expire_action_data'] = null;
            //     }
            // }
            // once again, we need to transform the structure of 'files' -- from formulate, we'll have array of objects, hopefully with our ID in there
            // we need to pull out the ID and just send back a flat list of IDs to our backend
            let { files, ...otherData } = saveData;
            const dataForApi = cloneDeep(otherData);  // copy this first, then we'll add files back in after 
            if (files && Array.isArray(files) && files.length) {
                let fileIdsForApi = files.map(f => f.id);
                dataForApi['file_ids'] = fileIdsForApi;  // as long as 'file_ids' is still the key you want it as when posting to API
            }
            // make sure we have vendor_id in there also
            // ADDED IN BACKEND NOW: dataForApi['vendor_id'] = this.vendorId;
            return { data: dataForApi, errors: errors };
        },
        setSavingStatus(isSaving) {
            this.saving = isSaving;
            this.$emit('loading-status', isSaving);
        },
        async handleSubmit(formData) {  // called on the FormulateForm @submit event
            // this should be async, return a Promise, so Formulate shows isLoading while loading
            console.log('*** From handleSubmit, here is formData we got:');
            console.log(formData);
            let { data: dataToSubmit, errors: formErrors } = this.formatDataForSave(formData);
            console.log('*** Here is data we are going to submit to API:', dataToSubmit);
            if (formErrors.length) {
                this.formErrors = formErrors;
                return;  // don't submit if we have errors - should we do something else here to tell form it didn't succeed?
            }
            this.setSavingStatus(true);
            let returnData;
            try {
                if (this.isUpdate) {
                    // TODO - this needs work if you actually use it for updates -- but you should probably use flow object stuff for updates
                    // NOTE - we don't allow updates to versions right now... but maybe later...
                    const subId = this.existingData.id;
                    console.log('subId', subId)
                    let resp = {};
                    returnData = resp.data;
                    console.log('got response from updating submission', returnData);
                } else {  // if NOT an update, create new submission
                    const resp = await suppliosApi.vendorCompItemStd.create_new_submission(this.vendorCompItemId, dataToSubmit);
                    returnData = resp.data;
                    console.log('got response from creating new submission (only)', returnData);
                }
            } catch (err) {
                this.apiErrorHandler(err);
            }
            if (returnData) {
                if (this.isUpdate) {
                    this.$emit('updated', returnData);
                } else {
                    this.$emit('created', returnData);
                }
                this.$emit('saved', returnData);  // common for both update/create
            }
            this.setSavingStatus(false);
            return returnData;  // make sure not to return until after you set saving status back to false
        },
        async formulateUploader(file, progress, error, options) {
            // pass this function to 'uploader' prop of formulate-input 
            // must return a Promise that resolves to array of objects, at least with 'url' key in each object (but you also expect 'id' for your own backend handling)
            // and must accept 4 args (file, progress, error, option)
            this.uploadPending = true;  // set our flag to true so we can block things while uploading
            let file_category = this.fileCategory;
            let parentId = null;  // need this is we allow uploading the file before the submission is created!
            let title = '';  // you could pass title to asyncSplFileSlinger if you know it in advance
            let uploadResponse = await asyncSplFileSlinger(file_category, file, progress, error, options, title, parentId);
            this.uploadPending = false;
            console.log('Dumping resp from our file uploader:');
            console.log(uploadResponse);
            return uploadResponse;  // must return a promise here so formulate knows upload is done 
        },
    }
}
</script>

<style>

</style>